/**
 * Cache utility for managing localStorage-based caching with invalidation support
 */

export interface CacheOptions {
  ttl?: number; // Time to live in milliseconds
  version?: string; // Version for cache invalidation
}

export class CacheManager {
  private static readonly DEFAULT_TTL = 30 * 60 * 1000; // 30 minutes

  /**
   * Set data in cache with optional TTL and version
   */
  static set<T>(key: string, data: T, options: CacheOptions = {}): void {
    const cacheData = {
      data,
      timestamp: Date.now(),
      ttl: options.ttl || this.DEFAULT_TTL,
      version: options.version || '1.0',
    };

    localStorage.setItem(key, JSON.stringify(cacheData));
  }

  /**
   * Get data from cache, returns null if expired or invalid
   */
  static get<T>(key: string, expectedVersion?: string): T | null {
    try {
      const cachedItem = localStorage.getItem(key);
      if (!cachedItem) {
        return null;
      }

      const cacheData = JSON.parse(cachedItem);
      const now = Date.now();
      
      // Check if cache has expired
      if (now - cacheData.timestamp > cacheData.ttl) {
        console.log(`Cache expired for key: ${key}`);
        this.remove(key);
        return null;
      }

      // Check version mismatch (for cache invalidation)
      if (expectedVersion && cacheData.version !== expectedVersion) {
        console.log(`Cache version mismatch for key: ${key}, expected: ${expectedVersion}, got: ${cacheData.version}`);
        this.remove(key);
        return null;
      }

      console.log(`Cache hit for key: ${key}`);
      return cacheData.data;
    } catch (error) {
      console.error(`Error reading cache for key: ${key}`, error);
      this.remove(key);
      return null;
    }
  }

  /**
   * Remove specific cache entry
   */
  static remove(key: string): void {
    localStorage.removeItem(key);
    console.log(`Cache cleared for key: ${key}`);
  }

  /**
   * Clear all cache entries matching a pattern
   */
  static clearPattern(pattern: string): void {
    const keys = Object.keys(localStorage).filter(key => key.includes(pattern));
    keys.forEach(key => {
      localStorage.removeItem(key);
      console.log(`Cache cleared for key: ${key}`);
    });
  }

  /**
   * Check if cache exists and is valid
   */
  static has(key: string, expectedVersion?: string): boolean {
    return this.get(key, expectedVersion) !== null;
  }

  /**
   * Get cache info (for debugging)
   */
  static getInfo(key: string): any {
    try {
      const cachedItem = localStorage.getItem(key);
      if (!cachedItem) {
        return null;
      }

      const cacheData = JSON.parse(cachedItem);
      return {
        exists: true,
        timestamp: new Date(cacheData.timestamp).toISOString(),
        age: Date.now() - cacheData.timestamp,
        ttl: cacheData.ttl,
        version: cacheData.version,
        expired: Date.now() - cacheData.timestamp > cacheData.ttl,
      };
    } catch (error) {
      return { exists: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }
}

// Specific cache keys used throughout the app
export const CACHE_KEYS = {
  PREDEFINED_CONTROL_TYPES: 'predefinedControlTypes',
  CONTROL_TYPE_DETAILS: 'controlTypeDetails',
} as const;

// Cache versions - increment when data structure changes
export const CACHE_VERSIONS = {
  CONTROL_TYPES: '3.0', // Incremented again to force cache invalidation for debugging
} as const;